/**
 * Simulator Engine
 * Main orchestrator for the warehouse simulator
 * Manages operators, workflows, and metrics
 */

import {
  SimulatorConfig,
  SimulatorState,
  SimulatorStatus,
  SimulatorMetrics,
  WorkflowType,
  PreFlightReport,
  VirtualOperatorInfo,
  TimingConfig,
  createInitialMetrics,
  DEFAULT_SIMULATOR_CONFIG
} from '../types';
import { PreFlightValidator } from './PreFlightValidator';
import { LockManager, getSharedLockManager, resetSharedLockManager } from './LockManager';
import { VirtualOperator, VirtualOperatorFactory } from './VirtualOperator';
import { getSharedRateLimiter, resetSharedRateLimiter } from './RateLimiter';
import {
  PickingWorkflow,
  PackingWorkflow,
  ReceivingWorkflow,
  PutAwayWorkflow,
  CountingWorkflow,
  TransferWorkflow,
  CorrectionWorkflow,
  PosDeductionWorkflow
} from '../workflows';

// Allowed operator groups for simulator access
const SIMULATOR_ALLOWED_GROUPS = ['3']; // Only Sysadmin (operatorGroupId = 3)

type StateChangeCallback = (state: SimulatorState) => void;

export class SimulatorEngine {
  private config: SimulatorConfig;
  private status: SimulatorStatus = 'idle';
  private operators: VirtualOperator[] = [];
  private lockManager: LockManager | null = null;
  private preFlightReport: PreFlightReport | null = null;
  private metrics: SimulatorMetrics;
  private errors: string[] = [];
  private startTime: Date | null = null;
  private isRunning: boolean = false;
  private metricsInterval: ReturnType<typeof setInterval> | null = null;
  private workflowTasks: Map<WorkflowType, boolean> = new Map();
  private stateChangeCallbacks: StateChangeCallback[] = [];

  // Track processed orders to prevent re-processing in this session
  private processedPurchaseOrders: Set<number> = new Set();
  private processedSalesOrders: Set<number> = new Set();

  constructor(config?: Partial<SimulatorConfig>) {
    this.config = { ...DEFAULT_SIMULATOR_CONFIG, ...config };
    this.metrics = createInitialMetrics();
  }

  // ============================================================================
  // Public API
  // ============================================================================

  /**
   * Check if user has access to simulator
   */
  static canAccess(operatorGroupId: string): boolean {
    return SIMULATOR_ALLOWED_GROUPS.includes(operatorGroupId);
  }

  /**
   * Subscribe to state changes
   */
  onStateChange(callback: StateChangeCallback): () => void {
    this.stateChangeCallbacks.push(callback);
    return () => {
      const index = this.stateChangeCallbacks.indexOf(callback);
      if (index > -1) {
        this.stateChangeCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Get current state
   */
  getState(): SimulatorState {
    return {
      status: this.status,
      config: this.config,
      preFlightReport: this.preFlightReport || undefined,
      metrics: this.metrics,
      operators: this.operators.map(op => op.getInfo()),
      errors: [...this.errors],
      lastError: this.errors.length > 0 ? this.errors[this.errors.length - 1] : undefined
    };
  }

  /**
   * Check if a purchase order has already been processed in this session
   */
  isPurchaseOrderProcessed(orderNo: number): boolean {
    return this.processedPurchaseOrders.has(orderNo);
  }

  /**
   * Mark a purchase order as processed
   */
  markPurchaseOrderProcessed(orderNo: number): void {
    this.processedPurchaseOrders.add(orderNo);
    console.log(`[SimulatorEngine] Marked PO ${orderNo} as processed (${this.processedPurchaseOrders.size} total)`);
  }

  /**
   * Check if a sales order has already been processed in this session
   */
  isSalesOrderProcessed(orderNo: number): boolean {
    return this.processedSalesOrders.has(orderNo);
  }

  /**
   * Mark a sales order as processed
   */
  markSalesOrderProcessed(orderNo: number): void {
    this.processedSalesOrders.add(orderNo);
    console.log(`[SimulatorEngine] Marked SO ${orderNo} as processed (${this.processedSalesOrders.size} total)`);
  }

  /**
   * Get count of processed orders
   */
  getProcessedOrderCounts(): { purchaseOrders: number; salesOrders: number } {
    return {
      purchaseOrders: this.processedPurchaseOrders.size,
      salesOrders: this.processedSalesOrders.size
    };
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<SimulatorConfig>): void {
    this.config = { ...this.config, ...config };
    this.notifyStateChange();
  }

  /**
   * Initialize the simulator and run pre-flight validation
   */
  async initialize(): Promise<PreFlightReport> {
    try {
      this.setStatus('validating');

      // Check authorization
      if (!SimulatorEngine.canAccess(this.config.operatorGroupId)) {
        throw new Error('Operatørgruppen har ikke tilgang til simulator');
      }

      // Validate required config
      if (!this.config.companyNo) {
        throw new Error('CompanyNo er påkrevd');
      }

      // Demo-modus tenant-vakt (B1-B2-C7): blokker i pre-flight, ikke midt i loop
      this.assertDemoTenantAllowed();

      // Run pre-flight validation
      const validator = new PreFlightValidator(this.config.companyNo);
      this.preFlightReport = await validator.runFullValidation();

      this.setStatus('idle');
      this.notifyStateChange();

      return this.preFlightReport;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Ukjent feil under initialisering';
      this.addError(message);
      this.setStatus('error');
      throw error;
    }
  }

  /**
   * Execute auto-fix for a specific validation
   */
  async executeAutoFix(validationName: string): Promise<void> {
    if (!this.preFlightReport) {
      throw new Error('Kjør validering først');
    }

    const validation = this.preFlightReport.validations.find(v => v.name === validationName);
    if (!validation || !validation.canAutoFix || !validation.fixAction) {
      throw new Error('Denne valideringen kan ikke fikses automatisk');
    }

    try {
      this.setStatus('validating');
      await validation.fixAction();

      // Re-run validation
      await this.initialize();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Feil under auto-fix';
      this.addError(message);
      this.setStatus('error');
      throw error;
    }
  }

  /**
   * Execute all auto-fixes
   */
  async executeAllAutoFixes(): Promise<void> {
    if (!this.preFlightReport) {
      throw new Error('Kjør validering først');
    }

    const fixableValidations = this.preFlightReport.validations.filter(
      v => v.canAutoFix && v.fixAction && v.status !== 'pass'
    );

    for (const validation of fixableValidations) {
      try {
        if (validation.fixAction) {
          console.log(`[SimulatorEngine] Executing auto-fix for: ${validation.name}`);
          await validation.fixAction();
        }
      } catch (error) {
        console.error(`[SimulatorEngine] Auto-fix failed for ${validation.name}:`, error);
      }
    }

    // Re-run validation
    await this.initialize();
  }

  /**
   * Start the simulation
   */
  async start(): Promise<void> {
    if (this.isRunning) {
      console.warn('[SimulatorEngine] Simulator is already running');
      return;
    }

    if (!this.preFlightReport?.canStart) {
      throw new Error('Pre-flight validering mislyktes. Rett opp feil før simulering kan starte.');
    }

    // Belte og bukser: vakten kjøres også her i tilfelle config er endret etter initialize()
    this.assertDemoTenantAllowed();

    try {
      this.isRunning = true;
      this.setStatus('running');
      this.startTime = new Date();
      this.metrics = createInitialMetrics();
      this.metrics.startTime = this.startTime;
      this.errors = [];

      // Initialize lock manager
      this.lockManager = getSharedLockManager(this.config.companyNo);
      this.lockManager.start(this.config.safety.lockCleanupIntervalMinutes * 60000);

      // Create operators (demo-modus: bruk navngitte operatører fra manifestet
      // slik at PickedBy matcher seedede operatører — ellers som før)
      VirtualOperatorFactory.reset();
      this.operators = VirtualOperatorFactory.createOperators(
        this.config.operatorCount,
        this.config.demo?.enabled ? this.config.demo.operators : undefined
      );

      // Start metrics update interval
      this.startMetricsInterval();

      // Start workflows
      this.startWorkflows();

      this.notifyStateChange();
      console.log('[SimulatorEngine] Simulator started');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Feil ved start av simulator';
      this.addError(message);
      this.setStatus('error');
      throw error;
    }
  }

  /**
   * Pause the simulation
   */
  pause(): void {
    if (this.status !== 'running') return;
    this.setStatus('paused');
    console.log('[SimulatorEngine] Simulator paused');
  }

  /**
   * Resume the simulation
   */
  resume(): void {
    if (this.status !== 'paused') return;
    this.setStatus('running');
    console.log('[SimulatorEngine] Simulator resumed');
  }

  /**
   * Stop the simulation
   */
  async stop(): Promise<void> {
    if (!this.isRunning && this.status !== 'paused') {
      return;
    }

    try {
      this.setStatus('stopping');
      this.isRunning = false;

      // Stop metrics interval
      if (this.metricsInterval) {
        clearInterval(this.metricsInterval);
        this.metricsInterval = null;
      }

      // Stop all workflows
      this.workflowTasks.clear();

      // Stop all operators
      this.operators.forEach(op => op.stop());

      // Release all locks
      if (this.lockManager) {
        this.lockManager.stop();
        await this.lockManager.releaseAllLocks();
      }

      this.setStatus('stopped');
      console.log('[SimulatorEngine] Simulator stopped');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Feil ved stopping av simulator';
      this.addError(message);
      this.setStatus('error');
    }
  }

  /**
   * Reset the simulator to initial state
   */
  async reset(): Promise<void> {
    await this.stop();

    // Reset rate limiter
    resetSharedRateLimiter();

    // Reset lock manager
    await resetSharedLockManager();

    // Reset state
    this.preFlightReport = null;
    this.metrics = createInitialMetrics();
    this.operators = [];
    this.errors = [];
    this.startTime = null;
    this.processedPurchaseOrders.clear();
    this.processedSalesOrders.clear();

    this.setStatus('idle');
    console.log('[SimulatorEngine] Simulator reset');
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  private setStatus(status: SimulatorStatus): void {
    this.status = status;
    this.notifyStateChange();
  }

  private notifyStateChange(): void {
    const state = this.getState();
    this.stateChangeCallbacks.forEach(cb => cb(state));
  }

  /**
   * Demo-modus tenant-vakt (B1-B2-C7): når demo.enabled er satt MÅ companyNo stå
   * i demo.allowedCompanies. Hindrer at demo-profiler kjøres mot feil tenant.
   * No-op uten demo-config — null endring for eksisterende bruk.
   */
  private assertDemoTenantAllowed(): void {
    const demo = this.config.demo;
    if (!demo?.enabled) return;
    if (!demo.allowedCompanies.includes(this.config.companyNo)) {
      throw new Error(
        `Demo-modus: tenant '${this.config.companyNo}' er ikke i allowedCompanies ` +
        `[${demo.allowedCompanies.join(', ')}] — simulator nektes start`
      );
    }
  }

  private addError(message: string): void {
    // Demo-modus: feil kun til console — ingen røde UI-bannere i skjermdeling.
    // Config-feil blokkeres uansett i pre-flight/vaktene, ikke midt i demo.
    if (this.config.demo?.enabled) {
      console.error(`[SimulatorEngine] (demo, dempet) Error: ${message}`);
      return;
    }
    this.errors.push(message);
    console.error(`[SimulatorEngine] Error: ${message}`);
  }

  private startMetricsInterval(): void {
    this.metricsInterval = setInterval(() => {
      this.updateMetrics();
      this.checkSafetyLimits();
    }, 1000);
  }

  private updateMetrics(): void {
    if (!this.startTime) return;

    const now = new Date();
    this.metrics.elapsedTime = (now.getTime() - this.startTime.getTime()) / 1000;
    this.metrics.requestsPerSecond = getSharedRateLimiter().getRequestsPerSecond();

    if (this.lockManager) {
      this.metrics.lockedPLists = this.lockManager.getLockCount();
    }

    // Calculate error rate
    const totalOps = this.metrics.totalTransactions;
    const totalErrors = Object.values(this.metrics.workflows).reduce(
      (sum, w) => sum + w.errorCount,
      0
    );
    this.metrics.errorRate = totalOps > 0 ? (totalErrors / totalOps) * 100 : 0;

    this.notifyStateChange();
  }

  private checkSafetyLimits(): void {
    if (!this.isRunning) return;

    // Check max transactions
    if (this.metrics.totalTransactions >= this.config.safety.maxTransactionsPerSession) {
      console.warn('[SimulatorEngine] Max transactions reached, stopping...');
      this.stop();
      return;
    }

    // Check max duration
    if (this.startTime) {
      const durationMinutes = (Date.now() - this.startTime.getTime()) / 60000;
      if (durationMinutes >= this.config.safety.maxDurationMinutes) {
        console.warn('[SimulatorEngine] Max duration reached, stopping...');
        this.stop();
        return;
      }
    }
  }

  private startWorkflows(): void {
    const workflows: WorkflowType[] = ['picking', 'packing', 'receiving', 'putaway', 'counting', 'transfer', 'correction', 'posdeduction'];

    for (const workflow of workflows) {
      const config = this.config.workflows[workflow];
      if (config.enabled) {
        this.workflowTasks.set(workflow, true);
        this.runWorkflowLoop(workflow);
      }
    }
  }

  private async runWorkflowLoop(workflowType: WorkflowType): Promise<void> {
    console.log(`[SimulatorEngine] Starting ${workflowType} workflow loop`);

    // Smart backoff for when no work is available
    const BASE_INTERVAL = this.config.workflowIntervalMs;
    // Picking/receiving need faster retry since they depend on order generation
    const MAX_BACKOFF = (workflowType === 'picking' || workflowType === 'receiving') ? 15000 : 30000;
    let currentBackoff = BASE_INTERVAL;
    let noWorkCount = 0;

    while (this.workflowTasks.get(workflowType) && this.isRunning) {
      // Skip if paused
      if (this.status === 'paused') {
        await this.delay(1000);
        continue;
      }

      // Get available operator
      const operator = this.getAvailableOperator(workflowType);
      if (!operator) {
        await this.delay(Math.min(currentBackoff, 5000)); // Max 5s wait for operator
        continue;
      }

      try {
        operator.startTask(workflowType, `Processing ${workflowType}`);
        const result = await this.executeWorkflowWithResult(workflowType, operator);
        operator.completeTask();

        // Check if there was actual work done
        if (result.hadWork) {
          // Reset backoff when work was available
          currentBackoff = BASE_INTERVAL;
          noWorkCount = 0;
          this.metrics.workflows[workflowType].successCount++;
          this.metrics.totalTransactions++;
        } else {
          // Increase backoff when no work available (exponential)
          noWorkCount++;
          currentBackoff = Math.min(BASE_INTERVAL * Math.pow(1.5, noWorkCount), MAX_BACKOFF);
          console.log(`[SimulatorEngine] ${workflowType}: No work available, backoff: ${Math.round(currentBackoff/1000)}s`);
        }
      } catch (error) {
        operator.failTask();
        this.metrics.workflows[workflowType].errorCount++;

        const message = error instanceof Error ? error.message : 'Unknown error';
        console.error(`[SimulatorEngine] ${workflowType} workflow error:`, message);

        // Also increase backoff on errors
        noWorkCount++;
        currentBackoff = Math.min(BASE_INTERVAL * Math.pow(1.5, noWorkCount), MAX_BACKOFF);
      }

      // Wait before next iteration (using smart backoff)
      await this.delay(currentBackoff);
    }

    console.log(`[SimulatorEngine] Stopped ${workflowType} workflow loop`);
  }

  /**
   * Execute workflow and return result including whether work was available
   */
  private async executeWorkflowWithResult(
    workflowType: WorkflowType,
    operator: VirtualOperator
  ): Promise<{ hadWork: boolean; error?: string }> {
    const startTime = Date.now();

    switch (workflowType) {
      case 'picking': {
        const workflow = new PickingWorkflow(this.config, operator);
        const result = await workflow.execute();
        this.metrics.averageResponseTime = Date.now() - startTime;
        // Check if actual work was done (lines processed)
        const hadWork = result.linesProcessed > 0;
        // Surface config errors (regelgruppe etc.) even if success=true
        if (result.error) {
          const isConfigError = result.error.includes('regelgruppe') || result.error.includes('konfigurert');
          if (isConfigError) {
            // Show config errors in the error list and operator status
            // (demo-modus: kun console — bannere dempes, jf. addError)
            if (this.config.demo?.enabled) {
              console.error(`[SimulatorEngine] (demo, dempet) Config error: ${result.error}`);
            } else if (!this.errors.includes(result.error)) {
              this.errors.push(result.error);
              this.notifyStateChange();
            }
            operator.startTask('picking', result.error);
          }
          if (!result.success && !result.error.includes('Ingen tilgjengelige')) {
            return { hadWork, error: result.error };
          }
        }
        return { hadWork };
      }
      case 'packing': {
        const workflow = new PackingWorkflow(this.config, operator);
        const result = await workflow.execute();
        this.metrics.averageResponseTime = Date.now() - startTime;
        // Only count as "hadWork" if the workflow completed successfully
        // This ensures successCount only increments for actually completed shipments
        const hadWork = result.success && (result.packagesProcessed > 0 || result.ordersProcessed > 0);
        if (!result.success && result.error && !result.error.includes('Ingen')) {
          return { hadWork: false, error: result.error };
        }
        return { hadWork };
      }
      case 'receiving': {
        const workflow = new ReceivingWorkflow(this.config, operator, this);
        const result = await workflow.execute();
        this.metrics.averageResponseTime = Date.now() - startTime;
        const hadWork = result.linesProcessed > 0;
        if (!result.success && result.error && !result.error.includes('Ingen')) {
          return { hadWork, error: result.error };
        }
        return { hadWork };
      }
      case 'putaway': {
        const workflow = new PutAwayWorkflow(this.config, operator);
        const result = await workflow.execute();
        this.metrics.averageResponseTime = Date.now() - startTime;
        const hadWork = result.linesProcessed > 0;
        if (!result.success && result.error && !result.error.includes('Ingen')) {
          return { hadWork, error: result.error };
        }
        return { hadWork };
      }
      case 'counting': {
        const workflow = new CountingWorkflow(this.config, operator);
        const result = await workflow.execute();
        this.metrics.averageResponseTime = Date.now() - startTime;
        const hadWork = result.itemsCounted > 0;
        if (!result.success && result.error && !result.error.includes('Ingen')) {
          return { hadWork, error: result.error };
        }
        return { hadWork };
      }
      case 'transfer': {
        const workflow = new TransferWorkflow(this.config, operator);
        const result = await workflow.execute();
        this.metrics.averageResponseTime = Date.now() - startTime;
        const hadWork = result.transfersCompleted > 0;
        if (!result.success && result.error && !result.error.includes('Ikke nok')) {
          return { hadWork, error: result.error };
        }
        return { hadWork };
      }
      case 'correction': {
        const workflow = new CorrectionWorkflow(this.config, operator);
        const result = await workflow.execute();
        this.metrics.averageResponseTime = Date.now() - startTime;
        const hadWork = result.correctionsCompleted > 0;
        if (!result.success && result.error && !result.error.includes('Ingen')) {
          return { hadWork, error: result.error };
        }
        return { hadWork };
      }
      case 'posdeduction': {
        const workflow = new PosDeductionWorkflow(this.config, operator);
        const result = await workflow.execute();
        this.metrics.averageResponseTime = Date.now() - startTime;
        const hadWork = result.deductionsCompleted > 0;
        if (!result.success && result.error && !result.error.includes('Ingen')) {
          return { hadWork, error: result.error };
        }
        return { hadWork };
      }
      default:
        throw new Error(`Unknown workflow type: ${workflowType}`);
    }
  }

  private getAvailableOperator(workflowType: WorkflowType): VirtualOperator | null {
    // First try to find a specialized operator
    const specialized = this.operators.find(
      op => op.isAvailable() && op.getSpecialization() === workflowType
    );
    if (specialized) return specialized;

    // Fall back to any available operator
    return this.operators.find(op => op.canHandle(workflowType)) || null;
  }

  private getTimingKey(workflowType: WorkflowType): keyof TimingConfig {
    switch (workflowType) {
      case 'picking': return 'pickTimeMs';
      case 'packing': return 'packTimeMs';
      case 'receiving': return 'receiveTimeMs';
      case 'putaway': return 'placeTimeMs';
      case 'counting': return 'countTimeMs';
      case 'transfer': return 'walkTimeMs';
      case 'correction': return 'countTimeMs'; // Correction uses count timing
      case 'posdeduction': return 'scanTimeMs'; // POS uses scan timing
      default: return 'walkTimeMs';
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Singleton instance
let sharedEngine: SimulatorEngine | null = null;

export const getSimulatorEngine = (config?: Partial<SimulatorConfig>): SimulatorEngine => {
  if (!sharedEngine) {
    sharedEngine = new SimulatorEngine(config);
  } else if (config) {
    sharedEngine.updateConfig(config);
  }
  return sharedEngine;
};

export const resetSimulatorEngine = async (): Promise<void> => {
  if (sharedEngine) {
    await sharedEngine.reset();
    sharedEngine = null;
  }
};
